import { useCallback } from 'react';
import { useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

// ==================== Types ====================

export interface Insurer {
  id: number;
  insurer_number: string;
  sub_number: number;
  insurer_name: string;
  insurer_type_id: number;
  insurer_type: { id: number; name: string } | null;
  cost_rate: number;
  family_cost_rate: number;
  is_enabled_invoice: boolean | null;
  is_nonoutput_rezept: boolean;
  medix_claim_type_id: number | null;
  available_from: string;
  available_to: string;
}

export const INSURER_SEARCH_PAGE_SIZE = 50;

export interface InsurerSearchPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

/** API envelope: { success, message, data } where data may be { data: Insurer[], pagination? } when paginated */
interface InsurersSearchResponse {
  success?: boolean;
  data: {
    data: Insurer[];
    pagination?: InsurerSearchPagination;
  };
}

export type SearchInsurersVariables =
  | string
  | { insurerNumber: string; page?: number; perPage?: number };

export type SearchInsurersResult =
  | Insurer[]
  | { data: Insurer[]; pagination: InsurerSearchPagination };

// ==================== Hooks ====================

export interface UseSearchInsurersOptions {
  /** When true, request minimal list (id, insurer_number, sub_number, insurer_name, insurer_type) for table display; select then uses GET /insurers/{id} for form. */
  listOnly?: boolean;
  /** Health insurance card lookup: exclude medical-aid-only insurer types. */
  healthInsuranceOnly?: boolean;
}

function isPaginatedVariables(
  v: SearchInsurersVariables
): v is { insurerNumber: string; page?: number; perPage?: number } {
  return typeof v === 'object' && v !== null && 'insurerNumber' in v;
}

export function buildInsurerSearchParams(
  insurerNumber: string,
  options?: {
    listOnly?: boolean;
    healthInsuranceOnly?: boolean;
    page?: number;
    perPage?: number;
  }
) {
  const usePaging = !!options?.listOnly && options?.perPage != null && options.perPage > 0;

  return {
    insurer_number: insurerNumber,
    ...(options?.healthInsuranceOnly ? { health_insurance_only: 1 } : {}),
    ...(options?.listOnly ? { list: 1 } : {}),
    ...(usePaging ? { page: options?.page ?? 1, page_num: options.perPage } : {}),
  };
}

export function buildInfiniteInsurerSearchParams(
  insurerNumber: string | null,
  page: number,
  options?: {
    healthInsuranceOnly?: boolean;
  }
) {
  return {
    insurer_number: insurerNumber,
    ...(options?.healthInsuranceOnly ? { health_insurance_only: 1 } : {}),
    list: 1,
    page,
    page_num: INSURER_SEARCH_PAGE_SIZE,
  };
}

/**
 * Search for insurers by insurer number.
 * When listOnly and variables include page/perPage, requests paginated results and returns { data, pagination }.
 */
export function useSearchInsurers(
  siteType: string,
  clinicNumber: string,
  options?: UseSearchInsurersOptions
) {
  const listOnly = options?.listOnly ?? false;
  const healthInsuranceOnly = options?.healthInsuranceOnly ?? false;

  const mutation = useMutation({
    mutationFn: async (variables: SearchInsurersVariables): Promise<SearchInsurersResult> => {
      const insurerNumber = isPaginatedVariables(variables) ? variables.insurerNumber : variables;
      const page = isPaginatedVariables(variables) ? variables.page : undefined;
      const perPage = isPaginatedVariables(variables) ? variables.perPage : undefined;
      const response = await apiClient.get<InsurersSearchResponse>(
        clinicApiPath(siteType, clinicNumber, '/insurers/search'),
        {
          params: buildInsurerSearchParams(insurerNumber, {
            listOnly,
            healthInsuranceOnly,
            page,
            perPage,
          }),
        }
      );

      const inner = response.data?.data ?? { data: [] };
      const list = inner.data ?? [];
      const pagination = inner.pagination;

      if (listOnly && perPage != null && perPage > 0 && pagination) {
        return { data: list, pagination };
      }
      return list;
    },
  });

  /** Fetch a single page (0-based pageIndex) for list display; only when listOnly. */
  const fetchPage = useCallback(
    async (insurerNumber: string, pageIndex: number): Promise<Insurer[]> => {
      const response = await apiClient.get<InsurersSearchResponse>(
        clinicApiPath(siteType, clinicNumber, '/insurers/search'),
        {
          params: {
            ...buildInsurerSearchParams(insurerNumber, {
              listOnly: true,
              healthInsuranceOnly,
              page: pageIndex + 1,
              perPage: INSURER_SEARCH_PAGE_SIZE,
            }),
          },
        }
      );
      const inner = response.data?.data ?? { data: [] };
      return inner.data ?? [];
    },
    [siteType, clinicNumber, healthInsuranceOnly]
  );

  return { ...mutation, fetchPage };
}

/**
 * Infinite query for fetching paginated insurer search results.
 * Similar pattern to useAdminInfiniteClinicsList.
 */
export function useInfiniteInsurerSearch(
  siteType: string,
  clinicNumber: string,
  insurerNumber: string | null,
  options?: {
    healthInsuranceOnly?: boolean;
  }
) {
  const healthInsuranceOnly = options?.healthInsuranceOnly ?? false;

  return useInfiniteQuery({
    queryKey: [
      'insurers',
      'infinite-search',
      siteType,
      clinicNumber,
      insurerNumber,
      healthInsuranceOnly,
    ],
    queryFn: ({ pageParam = 1 }) =>
      apiClient.get<InsurersSearchResponse>(
        clinicApiPath(siteType, clinicNumber, '/insurers/search'),
        {
          params: buildInfiniteInsurerSearchParams(insurerNumber, pageParam as number, {
            healthInsuranceOnly,
          }),
        }
      ),
    select: data => ({
      pages: data.pages.map(response => ({
        data: response.data?.data?.data ?? [],
        pagination: response.data?.data?.pagination,
      })),
      pageParams: data.pageParams,
    }),
    getNextPageParam: lastPage => {
      const pagination = lastPage.data?.data?.pagination;
      if (pagination && pagination.current_page < pagination.last_page) {
        return pagination.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!siteType && !!clinicNumber && !!insurerNumber?.trim(),
  });
}
